<template>
  <div
    class="pa-0 mx-md-auto justify-center flex-column profile-authentication"
  >
    <div class="form-title mb-6">
      {{ $t("Authentication") }}
    </div>
    <div class="d-flex change-pw-wrapper">
      <InputText
        class="col-12 col-md-7 col-sm-7 py-0 px-0"
        :label="$t('Email')"
        placeholder=" "
        readonly
        :model-value="userProfile.email"
      />
      <Button
        class="col ml-md-3 mr-0 ml-0 ml-sm-3"
        @click="userEditEmailDialog = true"
      >
        <v-icon start>
          mdi-email-edit-outline
        </v-icon>
        {{ $t("Edit email") }}
      </Button>
    </div>
    <div class="d-flex change-pw-wrapper">
      <InputText
        class="col-12 col-md-7 col-sm-7 py-0 px-0"
        type="password"
        :label="$t('Password')"
        placeholder=" "
        readonly
        model-value="******************"
      />
      <Button
        class="col ml-md-3 mr-0 ml-0 ml-sm-3"
        @click="userEditPasswordDialog = true"
      >
        <v-icon start>
          mdi-form-textbox-password
        </v-icon>
        {{ $t("Edit password") }}
      </Button>
    </div>
    <div class="text-lg-h6 text-uppercase mt-4 mb-4">
      {{ $t("Additional Email Addresses") }}
    </div>
    <div class="mb-4">
      {{
        $t(
          "Data shared with the following emails will be received to this account:"
        )
      }}
    </div>
    <div>
      <div
        v-for="(email, index) in additionalEmails"
        :key="index"
      >
        <div class="d-flex">
          <InputText
            v-model="additionalEmails[index]"
            :label="$t('Additional Email Address')"
            readonly
            :rules="emailRules"
          />
          <div>
            <Button
              class="col ma-1 text-primary col ml-md-3 mr-0 ml-0 ml-sm-3"
              :icon-btn="true"
              :icon-with-background="true"
              variant="filled"
              small
              @click="deleteAdditionalEmail(additionalEmails[index])"
            >
              <v-icon
                class="icon-flipped"
              >
                mdi-delete
              </v-icon>
            </Button>
          </div>
        </div>
      </div>
    </div>
    <div class="d-flex mt-2">
      <InputText
        v-model="additionalEmailAdressToAdd"
        class="col-12 col-md-7 col-sm-7 py-0 px-0"
        :label="$t('Email address')"
        :rules="emailRules"
      />
      <Button
        class="col ml-md-3 mr-0 ml-0 ml-sm-3"
        color="primary"
        :disabled="!validAdditionalEmail"
        @click="addAdditionalEmail()"
      >
        {{ $t("Add") }}
      </Button>
    </div>
    <div class="d-flex">
      <Button
        class="col mr-3"
        color="secondary"
        :scroll="true"
        @click="$emit('goto', 'personal-data')"
      >
        <v-icon start>
          mdi-chevron-left
        </v-icon>
        {{ $t("Previous") }}
      </Button>
      <Button
        class="col"
        color="primary"
        :scroll="true"
        @click="$emit('goto', 'notification')"
      >
        {{ $t("Next") }}
        <v-icon end>
          mdi-chevron-right
        </v-icon>
      </Button>
    </div>
    <v-dialog
      v-model="userEditPasswordDialog"
      max-width="890px"
      content-class="user-edit-pw-dialog"
    >
      <v-card :attach="true">
        <v-card-title>
          <v-spacer />
          <Button
            :icon-btn="true"
            color="text-black"
            @click="userEditPasswordDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </Button>
        </v-card-title>
        <iframe
          :src="passwordPage"
          :title="$t('Personal Data')"
          width="100%"
          height="700"
        />
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="userEditEmailDialog"
      max-width="890px"
    >
      <v-card>
        <v-card-title>
          <v-spacer />
          <Button
            :icon-btn="true"
            color="text-black"
            @click="userEditEmailDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </Button>
        </v-card-title>
        <iframe
          :src="emailPage"
          :title="$t('Personal Data')"
          width="100%"
          height="500"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import Button from "@/components/input-components/Button.vue";
import InputText from "@/components/input-components/InputText.vue";
import Config from "@/config/config";
import NotificationMessageService from "@/services/notificationmessage.service";
import { EMAIL_REG_EXP } from "@/utils/constants";
import { ProfileService } from "@/services/profile.service";
import { useI18n } from 'vue-i18n'

defineOptions({ name: 'ProfileAuthentication' })

const props = defineProps<{
  userProfile: {
    email?: string
    additionalEmail?: string[]
    [key: string]: any
  }
}>()

const emit = defineEmits<{
  goto: [section: string]
  reloadProfile: []
}>()

const { t, locale } = useI18n()

const userEditPasswordDialog = ref(false)
const userEditEmailDialog = ref(false)
const additionalEmailAdressToAdd = ref('')
const profileService = new ProfileService()

const emailRules = [
  (v: string) =>
    !v ||
    EMAIL_REG_EXP.test(v) ||
    t('This field must contain a valid email'),
]

const passwordPage = computed(() => {
  if (userEditPasswordDialog.value) {
    const url = new URL('account/password', Config.KEYCLOAK_REALM_URL)
    url.searchParams.append('kc_locale', locale.value)
    const parentUrl = new URL(parent.document.URL)
    url.searchParams.append('url', parentUrl.origin)
    return url.href
  }
  return 'about:blank'
})

const emailPage = computed(() => {
  if (userEditEmailDialog.value) {
    const url = new URL('request-email-change/', Config.KEYCLOAK_REALM_URL)
    url.searchParams.append('kc_locale', locale.value)
    return url.href
  }
  return 'about:blank'
})

const validAdditionalEmail = computed(() =>
  EMAIL_REG_EXP.test(additionalEmailAdressToAdd.value)
)

const additionalEmails = computed(() => props.userProfile.additionalEmail ?? [])

watch(userEditPasswordDialog, (newVal) => {
  if (!newVal) emit('reloadProfile')
})

function closeEditPasswordDialog(event: MessageEvent) {
  const url = new URL(Config.KEYCLOAK_BASE_URL)
  if (event.origin !== url.origin) return

  if (event.data === 'success') {
    userEditPasswordDialog.value = false
    NotificationMessageService.showSuccess('Your password has been updated.')
  }
}

onMounted(() => {
  window.addEventListener('message', closeEditPasswordDialog)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', closeEditPasswordDialog)
})

function addAdditionalEmail() {
  profileService
    .postAdditionalEmails(additionalEmailAdressToAdd.value)
    .then((response) => {
      const msg = response.response?.data?.message || t('You will receive an E-Mail. There you can add this E-Mail to your Account.') as string
      NotificationMessageService.showSuccess(msg)
      additionalEmailAdressToAdd.value = ''
      emit('reloadProfile')
    })
    .catch((error) => {
      const msg = error.response?.data?.message || t('The e-mail you entered is already registered.') as string
      NotificationMessageService.showError(msg)
    })
}

function deleteAdditionalEmail(email: string) {
  profileService
    .deleteAdditionalEmails(email)
    .then(() => {
      NotificationMessageService.showSuccess(t('E-Mail has been deleted!') as string)
      emit('reloadProfile')
    })
    .catch(() => {
      NotificationMessageService.showError(
        t('Could not delete additional E-Mail.') as string
      )
    })
}
</script>

<style lang="scss">
.user-edit-pw-dialog {
  align-self: flex-end;
}

.profile-authentication {
  .v-icon.v-icon {
    &.mdi-eye-off,
    &.mdi-eye-off::after {
      display: none;
    }
  }

  @media #{map.get($display-breakpoints, 'xs-only')} {
    .change-pw-wrapper {
      flex-direction: column;
      margin-bottom: 2rem;

      > .v-btn {
        padding: 0.75rem;
      }
    }
  }
}
</style>
